import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import PasswordVisibilityIcon from "../components/PasswordVisibilityIcon";
import { loginUser } from "../services/authService";
import { useAuth } from "../store/AuthContext";
import { formatAccountLockoutMessage } from "../utils/dateTimeFormat";
import { getHomeRouteByRole } from "../utils/roleRoutes";

function formatLoginApiError(err) {
  const d = err.response?.data;
  if (!d) {
    if (err.code === "ERR_NETWORK" || err.message === "Network Error") {
      return "Cannot reach the API. Check the server and REACT_APP_API_BASE_URL.";
    }
    return err.message || "Login failed.";
  }
  if (d.code === "account_locked" || d.lockout_until) {
    const localLockout = formatAccountLockoutMessage(d.lockout_until);
    if (localLockout) return localLockout;
    if (Array.isArray(d.non_field_errors) && d.non_field_errors.length) {
      return d.non_field_errors.join(" ");
    }
  }
  if (typeof d.detail === "string") return d.detail;
  if (Array.isArray(d.detail) && d.detail.length) return String(d.detail[0]);
  if (Array.isArray(d.non_field_errors) && d.non_field_errors.length) return d.non_field_errors.join(" ");
  if (d.email?.[0]) return String(d.email[0]);
  if (d.password?.[0]) return String(d.password[0]);
  return "Login failed.";
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, logout } = useAuth();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Ensure stale JWT values never interfere with fresh login (storage + React auth state).
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      logout();
      const { data } = await loginUser({
        ...form,
        email: String(form.email || "").trim().toLowerCase(),
      });
      login(data);
      navigate(getHomeRouteByRole(data?.user?.role, data?.user), { replace: true });
    } catch (err) {
      setError(formatLoginApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrap">
      <form className="card auth-card stack" onSubmit={onSubmit} style={{ position: "relative" }}>
        <button
          type="button"
          className="close-btn"
          onClick={() => navigate("/")}
          aria-label="Close sign in"
        >
          ×
        </button>
        <h2>Sign In</h2>
        {error && <p className="msg msg-error">{error}</p>}
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Verify email (registration OTP) before first sign-in.
        </p>
        <input
          type="email"
          placeholder="Email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          required
          autoComplete="email"
        />
        <div className="password-field password-field--inset">
          <input
            type={showPassword ? "text" : "password"}
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? "Hide password" : "Show password"}
            aria-pressed={showPassword}
            tabIndex={0}
          >
            <PasswordVisibilityIcon visible={showPassword} />
          </button>
        </div>
        <button type="submit" disabled={loading}>{loading ? "Signing in..." : "Login"}</button>
        <p style={{ margin: 0 }}>
          <Link to="/forgot-password">Forgot password?</Link>
        </p>
        <p style={{ margin: 0 }}>
          <Link to="/signup">Create account</Link>
          {" · "}
          <Link to="/">Home</Link>
        </p>
      </form>
    </div>
  );
}
